import { Logger } from '~~/helpers/logger';
import { getMockOrderHistory } from '~~/helpers/myOrders/getMockOrderHistory';
import type { OrderHistoryEntry, CancelOrderResponse } from '~~/types/myaccount/OrderHistory';
import { useOrderHistoryMock } from '~/composables/useOrderHistoryMock';

export const useMyOrders = () => {

  type OrderHistoryResponse = OrderHistoryEntry[] | null;

  type CancelOrderApiResponse = CancelOrderResponse | null;

  const { isMocking } = useOrderHistoryMock();

  const loading = useState<boolean>('orderHistoryLoading', () => false);
  const orderHistory = useState<OrderHistoryEntry[] | null>('orderHistory', () => null);
  const error = useState<string | null>('orderHistoryError', () => null);

  const load = async () => {

    try {

      loading.value = true;
      error.value = null;

      if (orderHistory.value?.length) {

        return;

      }

      if (isMocking.value) {

        orderHistory.value = getMockOrderHistory();
        Logger.info('my-orders|fetch: Mocked orders served instead of the MyOrders API');

        return;

      }

      const response = await $fetch<OrderHistoryResponse>('/my-orders/getOrderHistory');

      orderHistory.value = response ?? [];
      Logger.info('my-orders|fetch: Orders response received');

    } catch (e) {

      error.value = 'Failed to load orders. Please try again later.';
      orderHistory.value = [];
      Logger.error(`my-orders|fetch: Failed to fetch orders - ${e}`);

    } finally {

      loading.value = false;

    }

  };

  const getOrderByNumber = (orderNumber: string): OrderHistoryEntry | undefined => {

    if (!orderHistory.value) {

      return undefined;

    }

    return orderHistory.value.find((order) =>
      order.orderNumber === orderNumber,
    );

  };

  const cancelOrder = async (lwOrderId: OrderHistoryEntry['lwOrderId']): Promise<CancelOrderResponse> => {

    try {

      const response = await $fetch<CancelOrderApiResponse>('/my-orders/cancelOrder', {
        method: 'POST',
        body: { order_id: lwOrderId },
      });

      Logger.info(`my-orders|cancel: Cancel order response received for ${lwOrderId}`);

      return response ?? { success: false };

    } catch (e) {

      Logger.error(`my-orders|cancel: Failed to cancel order ${lwOrderId} - ${e}`);

      return { success: false };

    }

  };

  const clearOrderHistory = () => {

    orderHistory.value = null;
    error.value = null;

  };

  const hasLoadedOrders = computed(() => orderHistory.value !== null);

  return {
    loading,
    orderHistory,
    error,
    load,
    getOrderByNumber,
    cancelOrder,
    hasLoadedOrders,
    clearOrderHistory,
  };

};
